import { LocationWizardData } from './types';
import { validateStep, StepErrors } from './stepValidation';

/**
 * Pure helpers extracted from {@link useCreateLocationWizard} so the wizard's
 * trickier persistence/initialization rules can be unit-tested in isolation
 * (the hook itself is hard to test directly because it wires together routing,
 * context, and network calls).
 */

/**
 * Result of checking whether a step would actually invoke a network save if
 * `runStep` were called on it right now. See {@link getStepPersistability}.
 */
export interface StepPersistability {
  /** True only when the step is dirty, has any required prerequisites, and either passes validation or is allowed to save incomplete. */
  willPersist: boolean;
  /** Populated only when required-field validation *blocked* the save. Never set when `allowIncomplete` let it through. */
  validationErrors?: StepErrors;
  /** True when the step is persisting despite missing required fields. */
  incomplete?: boolean;
  /** Populated when the step blocks for a non-field reason (no location id yet, manager missing a phone number). */
  blockingMessage?: string;
}

/**
 * Single source of truth for whether persisting `stepIndex` would actually
 * reach `saveStepToBackend` — i.e. it's dirty, passes required-field
 * validation, has a saved location id if it needs one, and (for the Manager
 * step) has a manager with a phone number.
 *
 * `runStep` and `persistPendingSteps` both need this answer for different
 * reasons: `runStep` uses it to decide whether to surface validation errors
 * / a blocking message and whether to call `saveStepToBackend` at all;
 * `persistPendingSteps` uses it to decide whether the whole batch needs to
 * show the Busy overlay at all. Duplicating these conditions in both places
 * risked them drifting out of sync — e.g. `persistPendingSteps` showing Busy
 * for a batch where every "dirty" step turns out to be validation- or
 * prerequisite-blocked and no network call ever actually fires.
 *
 * @param options.allowIncomplete Let the step save even with required fields
 *        missing (Save and Close → Save). A draft is incomplete by definition,
 *        so required fields gate *submission*, not persistence. Step 1 of a
 *        location that does not exist yet is the exception: it POSTs, and the
 *        API rejects a blank name, phone or street address.
 */
export const getStepPersistability = (
  stepIndex: number,
  data: LocationWizardData,
  isDirty: boolean,
  hasLocationId: boolean,
  options: { allowIncomplete?: boolean } = {}
): StepPersistability => {
  const errors = validateStep(stepIndex, data);
  const incomplete = Object.keys(errors).length > 0;
  const mustBeComplete = !options.allowIncomplete || (stepIndex === 0 && !hasLocationId);

  if (incomplete && mustBeComplete) {
    return { willPersist: false, validationErrors: errors };
  }

  // Every step after the first needs a saved location id.
  if (stepIndex >= 1 && !hasLocationId) {
    return { willPersist: false, blockingMessage: 'Please complete Step 1 first.' };
  }

  // The Manager step requires the selected manager to have a phone number.
  if (stepIndex === 2 && data.locationManager && !data.locationManager.phoneNumber) {
    return {
      willPersist: false,
      blockingMessage:
        'The selected location manager is missing a phone number. Please choose a different manager or update their profile first.',
    };
  }

  if (!isDirty) {
    return { willPersist: false };
  }

  return { willPersist: true, incomplete };
};

/**
 * Fields owned exclusively by the Pricing step (Step 5). When merging an API
 * response from a non-pricing step, these are carried forward from the previous
 * local state so the supplier's in-progress pricing edits (or the values loaded
 * for edit) are not clobbered by defaults the API echoes back.
 */
export const preservePricingFields = (
  prev: LocationWizardData
): Pick<
  LocationWizardData,
  | 'pricePerDay'
  | 'pricePerMonth'
  | 'overageRate'
  | 'chargeOverages'
  | 'qrScannerAvailable'
  | 'locationEquipmentTypes'
  | 'live'
> => ({
  pricePerDay: prev.pricePerDay,
  pricePerMonth: prev.pricePerMonth,
  overageRate: prev.overageRate,
  chargeOverages: prev.chargeOverages,
  qrScannerAvailable: prev.qrScannerAvailable,
  locationEquipmentTypes: prev.locationEquipmentTypes,
  live: prev.live,
});

/**
 * Fields owned by later steps (Location Settings — Step 2 — and Features — Step
 * 4) that the admin "Autofill Location" enrichment can pre-populate while the
 * user is still on Step 1.
 *
 * The Step 1 save only sends Step-1 fields, so the API echoes these back as
 * empty defaults. Carrying them forward from the previous local state ensures
 * autofilled details (description, 24/7 access, space counts, managed-location
 * settings, and features) survive when advancing to — or jumping to — a later
 * step, instead of being clobbered by the server's defaults.
 */
export const preserveEnrichmentFields = (
  prev: LocationWizardData
): Pick<
  LocationWizardData,
  | 'locationDescription'
  | 'instructions'
  | 'has247Access'
  | 'gateHours'
  | 'afterHoursAccessProcedure'
  | 'minNumberOfSpaces'
  | 'totalNumberOfSpaces'
  | 'managedLocation'
  | 'managedSpaces'
  | 'locationFeatures'
> => ({
  locationDescription: prev.locationDescription,
  instructions: prev.instructions,
  has247Access: prev.has247Access,
  gateHours: prev.gateHours,
  afterHoursAccessProcedure: prev.afterHoursAccessProcedure,
  minNumberOfSpaces: prev.minNumberOfSpaces,
  totalNumberOfSpaces: prev.totalNumberOfSpaces,
  managedLocation: prev.managedLocation,
  managedSpaces: prev.managedSpaces,
  locationFeatures: prev.locationFeatures,
});

/** Returns the managed-space value used when automated approvals are toggled. */
export const getManagedSpacesDefault = (managedLocation: boolean): number =>
  managedLocation ? 1 : 0;

/**
 * Resolves the `managedSpaces` value to send on a location save.
 *
 * The backend requires `managedSpaces` to be a positive integer only when
 * automated approvals are enabled:
 * - Automated approvals ON  → the entered count (when positive), else capacity,
 *   else 1.
 * - Automated approvals OFF → 0.
 */
export const resolveManagedSpaces = (
  data: Pick<LocationWizardData, 'managedLocation' | 'managedSpaces' | 'totalNumberOfSpaces'>
): number => {
  const isPositive = (n: number | null | undefined): n is number =>
    typeof n === 'number' && n > 0;

  if (data.managedLocation && isPositive(data.managedSpaces)) {
    return data.managedSpaces;
  }
  if (!data.managedLocation) {
    return 0;
  }
  if (isPositive(data.totalNumberOfSpaces)) {
    return data.totalNumberOfSpaces;
  }
  return 1;
};

/**
 * Whether arriving at Step 5 (Pricing) for the first time should default
 * "Allow Daily Bookings" to Yes (represented by a non-null `pricePerDay`).
 *
 * Only applies to new locations where auto-approve was enabled in Step 2 and the
 * supplier hasn't already chosen a daily price — auto-approve requires daily
 * bookings, so we pre-enable it for them. Edit mode is left untouched so an
 * existing listing's saved choice is preserved.
 */
export const shouldDefaultDailyBookings = (
  isEdit: boolean,
  data: Pick<LocationWizardData, 'managedLocation' | 'pricePerDay'>
): boolean => !isEdit && data.managedLocation && data.pricePerDay === null;


/**
 * Resolves what the wizard's Close button should do based on whether the
 * current step has unsynced edits:
 *
 * - `'prompt'` → the step is dirty, so show the save/discard dialog rather than
 *   silently dropping (or silently saving) the pending changes.
 * - `'exit'`   → nothing is unsynced, so leave to Manage Locations immediately.
 *
 * @param isDirty Whether the current step has UI changes not yet synced.
 */
export const resolveCancelIntent = (isDirty: boolean): 'prompt' | 'exit' =>
  isDirty ? 'prompt' : 'exit';

/**
 * Builds the ordered list of step indices that must be persisted before a
 * navigation action (step-indicator jump, Back, or Close → Save) proceeds.
 *
 * Cross-step mutations — namely the admin "Autofill Location" enrichment,
 * which is triggered from Step 1 but populates fields owned by Location
 * Settings (Step 2) and Features (Step 4) — mean more than one step can be
 * dirty at once. Persisting only the step being left silently stranded the
 * other dirty steps' edits (never PATCHed) and, worse, let a later step's own
 * save merge a stale server response over that still-unsynced local data,
 * blanking it out. Every navigation path must therefore save *all* dirty
 * steps, not just the current one.
 *
 * @param currentStep The step being navigated away from — always included so
 *        its own edits (if any) are captured, even when triggered by a jump
 *        to a non-adjacent step.
 * @param dirtySteps All step indices with unsynced edits (from any source —
 *        manual edits or enrichment).
 * @param options Ordering behavior for the returned list.
 * @param options.currentFirst When true, `currentStep` is placed first
 *        regardless of its index (used by Close → Save, which runs with
 *        validation enforced and must surface an invalid *visible* step's
 *        errors before ever attempting other steps). When false, the full
 *        set is sorted ascending (used by free step-indicator navigation and
 *        Back, which never block on validation) so Step 1 — which creates the
 *        location id every later step's save depends on — always runs first
 *        if it's still dirty and unpersisted.
 */
export const getPendingStepIndices = (
  currentStep: number,
  dirtySteps: Set<number>,
  options: { currentFirst: boolean }
): number[] => {
  const all = new Set(dirtySteps);
  all.add(currentStep);

  if (options.currentFirst) {
    const others = Array.from(all)
      .filter((step) => step !== currentStep)
      .sort((left, right) => left - right);
    return [currentStep, ...others];
  }

  return Array.from(all).sort((left, right) => left - right);
};


